// UnplacedReview.js — "What we heard": the check between one multi-patient
// recording and its notes.
//
// The main job is the pile at the top: lines the splitter could not place,
// one tap each to give them a patient. Below it, summaries grouped by patient
// and suggested section. Each summary still acts on its original source line.
//
// Its own modal rather than reactstrap's: the theme's modal is built for forms
// and reads blocky for a dense list like this one.
//
// Closing it is NOT "discard": unchecked lines use the AI's best guess,
// and every other change is kept. That is why the
// backdrop and Esc do nothing here.
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { IGNORE, bestGuessOf, choiceOf, ownerOf, suggestedOwner } from './reviewSession';
import { REVIEW_SECTIONS, summarizeReview } from './reviewSummaries';

const PATIENT_COLORS = ['#2b6cb0', '#0f7f83', '#7a5195', '#b7791f', '#c05621'];
const AMBER = '#975a16';
const MUTED = '#a0aec0';

const formatClock = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const formatTimestamp = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const firstName = (name) => String(name || '').split(' ')[0];

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2050,
    background: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  panel: {
    width: '100%',
    maxWidth: 720,
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
    color: '#1a202c',
    fontSize: 15,
    lineHeight: 1.45,
    textAlign: 'left',
    outline: 'none',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 20px 4px',
  },
  title: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', margin: 0, lineHeight: 1.2, color: '#1a202c' },
  meta: { fontSize: 12.5, color: '#718096', marginTop: 2 },
  close: {
    width: 34,
    height: 34,
    flexShrink: 0,
    borderRadius: '50%',
    border: 0,
    background: '#edf2f7',
    color: '#4a5568',
    fontSize: 18,
    lineHeight: '34px',
    padding: 0,
    cursor: 'pointer',
    appearance: 'none',
  },
  body: { padding: '8px 20px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 },
  pile: { background: '#fffaf0', border: '1px solid #f3dfb2', borderRadius: 10, padding: 8 },
  pileTitle: { fontSize: 13.5, fontWeight: 700, color: '#975a16' },
  pileNote: { fontSize: 12.5, color: '#975a16', margin: '1px 0 6px' },
  card: { background: '#fff', border: '1px solid #f3dfb2', borderRadius: 6, padding: '6px 8px', marginTop: 4 },
  cardText: { fontSize: 14, marginBottom: 4, minWidth: 0 },
  chips: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  moveTo: { fontSize: 12, color: '#718096', marginRight: 2 },
  done: {
    background: '#f0fff4',
    border: '1px solid #c6f6d5',
    borderRadius: 8,
    padding: '6px 10px',
  },
  doneTitle: { fontSize: 13.5, fontWeight: 600, color: '#276749' },
  sectionTitle: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#718096' },
  dot: (color) => ({ display: 'inline-block', width: 8, height: 8, flexShrink: 0, borderRadius: '50%', background: color }),
  sectionHint: { fontSize: 12, color: '#718096', margin: '8px 0 0' },
  row: (color, open, timeline, tentative) => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: 0,
    borderLeft: timeline ? `3px solid ${color}` : 0,
    background: timeline && tentative ? '#fffaf0' : open ? '#f7fafc' : 'transparent',
    borderRadius: timeline ? open ? '8px 8px 0 0' : 8 : open ? '0 8px 0 0' : '0 8px 8px 0',
    padding: '4px 8px',
    marginBottom: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    appearance: 'none',
  }),
  rowText: (struck) => ({
    fontSize: 14.5,
    color: struck ? MUTED : '#2d3748',
    textDecoration: struck ? 'line-through' : 'none',
  }),
  rowLine: { display: 'flex', alignItems: 'baseline', gap: 8 },
  rowTime: { marginLeft: 'auto', flexShrink: 0, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 12, color: MUTED },
  rowNote: { fontSize: 12, marginTop: 1 },
  tag: (color) => ({ fontWeight: 600, color }),
  actions: (color, timeline, tentative) => ({
    padding: '4px 8px 8px',
    marginBottom: timeline ? 0 : 4,
    background: timeline && tentative ? '#fffaf0' : '#f7fafc',
    borderLeft: timeline ? `3px solid ${color}` : 0,
    borderRadius: timeline ? '0 0 8px 8px' : '0 0 8px 0',
  }),
  patient: { borderTop: '1px solid #edf2f7', marginTop: 8, paddingTop: 4 },
  patientToggle: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', minHeight: 30, border: 0, background: 'transparent', padding: '2px 0', textAlign: 'left', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  chevron: { display: 'block', width: 14, height: 14, flexShrink: 0 },
  footer: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 20px', borderTop: '1px solid #edf2f7' },
  hint: { flex: 1, fontSize: 12.5, color: '#718096' },
  // Its own row under the hint and Done, so it fits a narrow side panel.
  skip: { flexBasis: '100%', border: 0, background: 'transparent', padding: 0, textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#2b6cb0', cursor: 'pointer', appearance: 'none' },
  tabs: { display: 'flex', gap: 6, margin: '10px 0 6px' },
  tab: (active) => ({ border: 0, borderRadius: 6, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: active ? '#2b6cb0' : '#718096', background: active ? '#ebf8ff' : '#f7fafc' }),
  timelinePatient: (color) => ({ fontSize: 12, fontWeight: 600, color }),
};

const chipStyle = (color, chosen) => ({
  borderRadius: 999,
  padding: '3px 11px',
  fontSize: 12.5,
  fontWeight: 600,
  lineHeight: 1.3,
  cursor: 'pointer',
  border: `1.5px solid ${chosen ? color : '#cbd5e0'}`,
  background: chosen ? color : '#fff',
  color: chosen ? '#fff' : color,
  appearance: 'none',
});

const quietButton = {
  border: 0,
  background: 'transparent',
  color: '#718096',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '3px 6px',
  cursor: 'pointer',
  appearance: 'none',
};

const UnplacedReview = ({
  apiClient,
  onAttributionHints,
  unplaced,
  placed,
  describeVisit,
  onChooseUnplaced,
  onMovePlaced,
  onRemovePlaced,
  onDone,
  onDismiss,
  busy,
}) => {
  const panelRef = useRef(null);
  const [openId, setOpenId] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [preparedReview, setPreparedReview] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [tab, setTab] = useState('patients');
  const hintsCallbackRef = useRef(onAttributionHints);
  hintsCallbackRef.current = onAttributionHints;
  const sourcesRef = useRef([]);
  sourcesRef.current = [...unplaced, ...placed].map((line) => ({
    ...line, names: Object.fromEntries(line.candidates.map((id) => [id, describeVisit(id, line)])),
  }));
  const sourceKey = sourcesRef.current.map((line) => line.id).sort().join('|');
  const summarizing = Boolean(apiClient?.summarizeMultiRecordReview) &&
    (preparedReview?.sourceKey !== sourceKey || preparedReview?.apiClient !== apiClient);

  useEffect(() => {
    let current = true;
    summarizeReview(apiClient, sourcesRef.current, (batch) => {
      if (!current) return false;
      setSummaries((previous) => ({ ...previous, ...batch }));
      hintsCallbackRef.current?.(batch);
      return true;
    }).finally(() => {
      if (current) setPreparedReview({ sourceKey, apiClient });
    });
    return () => { current = false; };
  }, [apiClient, sourceKey]);

  // Hold the page still behind the dialog, and start keyboard focus inside it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const visitIds = [];
  [...unplaced, ...placed].forEach((line) =>
    line.candidates.forEach((id) => {
      if (!visitIds.includes(id)) visitIds.push(id);
    }),
  );
  const colorFor = (id) => PATIENT_COLORS[visitIds.indexOf(id) % PATIENT_COLORS.length];
  const nameFor = (id, line) => describeVisit(id, line);

  const pile = unplaced.filter((line) => choiceOf(line) == null && suggestedOwner(line) == null);
  const rows = [
    ...placed.map((line) => ({
      line,
      kind: 'placed',
      owner: line.removed ? null : ownerOf(line),
      changed: line.removed || line.choice != null,
    })),
    ...unplaced.map((line) => ({
      line,
      kind: 'unplaced',
      owner: choiceOf(line) == null ? suggestedOwner(line) : choiceOf(line) === IGNORE ? null : line.choice,
      changed: line.choice != null,
    })),
  ].sort((a, b) => a.line.spokenAt - b.line.spokenAt || a.line.seq - b.line.seq);
  const changes = rows.filter((row) => row.changed).length;
  const times = rows.map((row) => row.line.spokenAt);
  const first = formatClock(Math.min(...times));
  const last = formatClock(Math.max(...times));
  const doneDisabled = busy || summarizing || pile.length > 0;
  // Skip can only place lines that have a best guess to place them on.
  const canSkip = !summarizing && pile.length > 0 && pile.every((line) => bestGuessOf(line) != null);

  const isPending = (row) => row.kind === 'unplaced' && choiceOf(row.line) == null && row.owner == null;
  // By patient shows clinical lines only. Small talk the clinician has not
  // touched stays on the timeline, where it can still be placed.
  const timelineOnly = (row) => row.line.nonclinical && !row.changed;
  const needsReview = (row) => row.owner != null && row.line.confidence === 'needs_review' &&
    row.line.choice == null && !row.line.confirmed && !row.line.nonclinical;
  const rowColor = (row) => {
    if (isPending(row)) return AMBER;
    return row.owner == null ? MUTED : colorFor(row.owner);
  };

  const move = (row, id) => {
    if (row.kind === 'unplaced') onChooseUnplaced(row.line.id, id);
    else onMovePlaced(row.line.id, id);
  };
  const remove = (row, removed) => {
    if (row.kind === 'unplaced') onChooseUnplaced(row.line.id, removed ? IGNORE : null);
    else onRemovePlaced(row.line.id, removed);
  };

  const renderRow = (row, timeline = false) => {
    const open = openId === row.line.id;
    const color = rowColor(row);
    const pending = isPending(row);
    const tentative = needsReview(row) || pending;
    const struck = row.owner == null && !isPending(row);
    const note =
      row.kind === 'placed' && row.line.choice != null
        ? `moved from ${firstName(nameFor(row.line.from, row.line))}`
        : row.kind === 'unplaced' && row.line.choice != null && row.owner != null
          ? 'placed by you'
          : null;
    return (
      <div key={row.line.id} style={timeline ? { marginBottom: 2 } : undefined}>
        <button
          type="button"
          aria-expanded={open}
          aria-description={timeline && tentative ? pending ? 'No patient assigned' : 'Attribution needs review' : undefined}
          title={timeline && tentative ? pending ? 'No patient assigned' : 'Attribution needs review' : undefined}
          onClick={() => setOpenId(open ? null : row.line.id)}
          style={S.row(color, open, timeline, tentative)}
        >
          <div style={S.rowLine}>
            {!timeline && <span
              role={tentative ? 'img' : undefined}
              aria-label={tentative ? 'Attribution needs review' : undefined}
              aria-hidden={tentative ? undefined : true}
              title={tentative ? 'Needs review' : undefined}
              style={{ ...S.dot(tentative ? '#b7791f' : '#cbd5e0'), alignSelf: 'flex-start', marginTop: 7 }}
            />}
            <span style={S.rowText(struck)}>
              {timeline && <span style={S.timelinePatient(color)}>{pending ? 'Unplaced' : row.owner == null ? 'Not in any note' : nameFor(row.owner, row.line)}<br /></span>}
              {timeline ? row.line.text : summaries[row.line.id]?.summary || row.line.text}
            </span>
            <span style={S.rowTime} title="Audio chunk start">{timeline ? formatTimestamp(row.line.spokenAt) : formatClock(row.line.spokenAt)}</span>
          </div>
          {note && <div style={{ ...S.rowNote, ...S.tag(color) }}>{note}</div>}
        </button>
        {open && (
          <div style={S.actions(color, timeline, tentative)}>
            <div style={S.chips}>
              <span style={S.moveTo}>Move to</span>
              {row.line.candidates.map((id) => (
                <button
                  key={id}
                  type="button"
                  title={nameFor(id, row.line)}
                  aria-pressed={row.owner === id}
                  disabled={busy}
                  onClick={() => move(row, id)}
                  style={chipStyle(colorFor(id), row.owner === id)}
                >
                  {firstName(nameFor(id, row.line))}
                </button>
              ))}
              {/* Small talk has nowhere to be restored to; a patient chip places it. */}
              {struck ? (row.kind === 'unplaced' && row.line.nonclinical ? null : (
                <button type="button" disabled={busy} onClick={() => remove(row, false)} style={quietButton}>
                  Restore
                </button>
              )) : (
                <button type="button" disabled={busy} onClick={() => remove(row, true)} style={quietButton}>
                  {row.kind === 'unplaced' ? 'Ignore' : 'Take out'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={S.overlay}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="what-we-heard-title"
        tabIndex={-1}
        style={S.panel}
      >
        <div style={S.header}>
          <div>
            <h2 id="what-we-heard-title" style={S.title}>
              Review what we heard
            </h2>
            <div style={S.meta}>
              One recording · {plural(visitIds.length, 'patient')} ·{' '}
              {first === last ? first : `${first}–${last}`}
            </div>
          </div>
          {/* Same as Skip; hidden while loading, when there are no best guesses yet. */}
          {!summarizing && (
            <button
              type="button"
              style={S.close}
              aria-label="Skip review — Comprehend places unchecked lines on its best guess"
              title="Skip review — unchecked lines use Comprehend’s best guess; your other changes are kept"
              onClick={onDismiss}
              disabled={busy}
            >
              ×
            </button>
          )}
        </div>

        <div style={S.body}>
          {summarizing ? (
            <div role="status" style={{ ...S.sectionHint, display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
              <Spinner size="sm" aria-hidden="true" /> Preparing summaries and attribution…
            </div>
          ) : <>
          {pile.length > 0 ? (
            <div style={S.pile}>
              <div style={S.pileTitle}>{plural(pile.length, 'line')} we couldn’t place</div>
              <div style={S.pileNote}>
                Check this over before we write the notes. Tap any line to fix the wording, move it to another patient, or take it out.
              </div>
              {pile.map((line) => (
                <div key={line.id} style={S.card}>
                  <div style={S.rowLine}>
                    <span style={S.cardText}>{line.text}</span>
                    <span style={S.rowTime}>{formatClock(line.spokenAt)}</span>
                  </div>
                  {bestGuessOf(line) != null && <div style={S.rowNote}>Best guess: {nameFor(bestGuessOf(line), line)}</div>}
                  <div style={S.chips}>
                    <span style={S.moveTo}>Move to</span>
                    {line.candidates.map((id) => (
                      <button
                        key={id}
                        type="button"
                        title={nameFor(id, line)}
                        disabled={busy}
                        onClick={() => onChooseUnplaced(line.id, id)}
                        style={chipStyle(colorFor(id), false)}
                      >
                        {firstName(nameFor(id, line))}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onChooseUnplaced(line.id, IGNORE)}
                      style={quietButton}
                    >
                      Ignore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={S.done}>
              <div style={S.doneTitle}>Nothing left unplaced</div>
            </div>
          )}

          <div role="tablist" aria-label="Recording review views" style={S.tabs}>
            {[['patients', 'By patient'], ['timeline', 'Timeline']].map(([id, label]) => (
              <button key={id} type="button" role="tab" id={`review-tab-${id}`} aria-selected={tab === id} aria-controls={`review-panel-${id}`} style={S.tab(tab === id)} onClick={() => setTab(id)}>{label}</button>
            ))}
          </div>
          <div role="tabpanel" id={`review-panel-${tab}`} aria-labelledby={`review-tab-${tab}`}>
          <p style={S.sectionHint}>
            {tab === 'patients' ? 'Suggested sections · Tap a line to reassign or take it out.' : 'Times mark audio chunk starts; lines in a chunk share a time.'}
            {tab === 'timeline' && rows.some((row) => row.line.orderApproximate) && ' Within-chunk order is approximate where source text could not be matched.'}
          </p>

          {tab === 'timeline' ? rows.map((row) => renderRow(row, true)) : [...visitIds, null].map((visitId) => {
            const patientRows = rows.filter((row) => row.owner === visitId && !isPending(row) && !timelineOnly(row));
            if (!patientRows.length) return null;
            const groupKey = visitId == null ? 'excluded' : visitId;
            const patientColor = visitId == null ? MUTED : colorFor(visitId);
            const reviewCount = patientRows.filter(needsReview).length;
            const patientFirst = formatClock(Math.min(...patientRows.map((row) => row.line.spokenAt)));
            const patientLast = formatClock(Math.max(...patientRows.map((row) => row.line.spokenAt)));
            return (
              <div key={groupKey} style={S.patient}>
                <button
                  type="button"
                  style={{ ...S.patientToggle, color: patientColor }}
                  aria-expanded={Boolean(expanded[groupKey])}
                  onClick={() => setExpanded((previous) => ({ ...previous, [groupKey]: !previous[groupKey] }))}
                >
                  <svg aria-hidden="true" viewBox="0 0 16 16" style={{ ...S.chevron, transform: expanded[groupKey] ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={S.dot(patientColor)} />
                  {visitId == null ? 'Not in any note' : nameFor(visitId, patientRows[0].line)}
                  <span style={{ ...S.rowTime, fontWeight: 400, flexShrink: 1, textAlign: 'right' }}>
                    {patientFirst === patientLast ? patientFirst : `${patientFirst}–${patientLast}`}
                    {reviewCount > 0
                      ? ` · ${reviewCount} to check`
                      // Only claim it once the AI check actually ran on every line.
                      : patientRows.every((row) => row.line.confidence) ? ' · nothing to check' : ''}
                  </span>
                </button>
                {expanded[groupKey] ? [...REVIEW_SECTIONS, null].map((section) => {
                  // null: lines with no clinical section (the summary request
                  // failed), listed last as they were spoken, with no heading.
                  const sectionOf = (row) => summaries[row.line.id]?.section;
                  const sectionRows = patientRows.filter((row) =>
                    section == null ? !REVIEW_SECTIONS.includes(sectionOf(row)) : sectionOf(row) === section);
                  if (!sectionRows.length) return null;
                  return (
                    <div key={section || 'unsectioned'} role="group" aria-label={section || undefined}>
                      {section && <div style={{ ...S.sectionTitle, margin: '6px 0 2px' }}>
                        {section === 'Treatment' ? 'Activity log / treatment' : section}
                      </div>}
                      {sectionRows.map((row) => renderRow(row))}
                    </div>
                  );
                }) : null}
              </div>
            );
          })}
          </div>
          </>}
        </div>

        <div style={S.footer}>
          <div style={S.hint}>
            {summarizing
              ? `Reviewing ${plural(rows.length, 'transcript line')}.`
              : pile.length > 0
              ? `${plural(pile.length, 'line')} up top ${pile.length === 1 ? 'needs' : 'need'} a patient first.`
              : changes > 0
                ? `${plural(changes, 'change')} will be saved to the visits.`
                : rows.some((row) => row.kind === 'unplaced' && row.owner != null)
                  ? 'Unchecked lines will use the AI’s best guess.'
                : 'Everything stays where it is.'}
          </div>
          {!summarizing && (
            <button
              type="button"
              disabled={doneDisabled}
              onClick={onDone}
              style={{
                flexShrink: 0,
                minWidth: 100,
                border: 0,
                borderRadius: 8,
                padding: '8px 20px',
                fontSize: 14.5,
                fontWeight: 600,
                background: doneDisabled ? '#e2e8f0' : '#2d3748',
                color: doneDisabled ? '#718096' : '#fff',
                cursor: doneDisabled ? 'default' : 'pointer',
                appearance: 'none',
              }}
            >
              {busy ? <Spinner size="sm" /> : 'Done'}
            </button>
          )}
          {canSkip && (
            <button type="button" disabled={busy} onClick={onDismiss} style={S.skip}>
              Skip this — let Comprehend place them
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnplacedReview;
